import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { tokenStorage, setUnauthorizedCallback } from '../services/api';
import type { User, LoginInput, RegisterInput } from '../types/auth.types';

// ── Context shape ────────────────────────────────────────────

interface AuthState {
  user: User | null;
  /** true while checking initial token on mount */
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthState | null>(null);

// ── Provider ─────────────────────────────────────────────────

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Guard against calling navigate after unmount
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ── Check stored token on mount ──────────────────────────
  useEffect(() => {
    const initAuth = async (): Promise<void> => {
      const token = tokenStorage.get();
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const me = await authService.getMe();
        if (isMounted.current) setUser(me);
      } catch {
        // Token is expired / invalid — clear it silently
        tokenStorage.remove();
      } finally {
        if (isMounted.current) setIsLoading(false);
      }
    };
    void initAuth();
  }, []);

  // ── 401 global handler ───────────────────────────────────
  useEffect(() => {
    setUnauthorizedCallback(() => {
      if (isMounted.current) {
        setUser(null);
        navigate('/login', { replace: true });
      }
    });
  }, [navigate]);

  // ── Actions ──────────────────────────────────────────────
  const login = useCallback(async (input: LoginInput): Promise<void> => {
    const { user: loggedInUser } = await authService.login(input);
    setUser(loggedInUser);
  }, []);

  const register = useCallback(async (input: RegisterInput): Promise<void> => {
    const { user: newUser } = await authService.register(input);
    setUser(newUser);
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    await authService.logout();
    setUser(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  const value: AuthState = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    login,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
